/**
 * Every route path in one place, so links are not spelled out as loose strings
 * across the app.
 *
 * The shape follows the wireframes: twelve sections, several with sub-pages.
 */
export const ROUTES = {
  home: '/',
  forbidden: '/forbidden',
  /**
   * The shared-components reference page, for the mobile developer and QA.
   * Reachable by any signed-in person, listed in no navigation or search.
   */
  components: '/components',

  signIn: '/sign-in',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  /**
   * The page an invitation link opens. Public: the person has no account yet,
   * and the token is what proves they were invited.
   *
   * Called with ':token' it is the route pattern, so the address and the
   * pattern cannot drift apart. `/invitations/` rather than `/set-password`
   * so it reads as what it is, and so a future "change my password while
   * signed in" screen does not collide with it.
   */
  invitation: (token: string) => `/invitations/${token}`,

  dashboard: '/dashboard',

  notifications: {
    root: '/notifications',
    all: '/notifications',
    drafts: '/notifications/drafts',
    scheduled: '/notifications/scheduled',
    sent: '/notifications/sent',
    new: '/notifications/new',
  },

  arc: {
    root: '/manage-arc',
    builder: '/manage-arc',
    volunteer: '/manage-arc/volunteer-recruitment',
    fundraising: '/manage-arc/fundraising',
  },

  campaigns: {
    root: '/campaigns',
    all: '/campaigns',
    active: '/campaigns/active',
    inactive: '/campaigns/inactive',
    new: '/campaigns/new',
  },

  petitions: {
    root: '/petitions',
    all: '/petitions',
    signatures: '/petitions/signatures',
    archived: '/petitions/archived',
    new: '/petitions/new',
  },

  events: {
    root: '/events',
    all: '/events',
    upcoming: '/events/upcoming',
    past: '/events/past',
    new: '/events/new',
  },

  guides: {
    root: '/guides',
    current: '/guides',
    endorsements: '/guides/endorsements',
    archived: '/guides/archived',
    new: '/guides/new',
  },

  surveys: {
    root: '/surveys',
    all: '/surveys',
    responses: '/surveys/responses',
    archived: '/surveys/archived',
    new: '/surveys/new',
  },

  pages: {
    root: '/pages',
    custom: '/pages',
    activistArc: '/pages/activistarc',
    new: '/pages/new',
    /**
     * One custom page's editor. Called with ':id' it is the route pattern, so
     * the address and the pattern cannot drift apart.
     */
    edit: (id: string) => `/pages/${id}`,
  },

  documents: {
    root: '/documents',
    library: '/documents',
    categories: '/documents/categories',
    upload: '/documents/upload',
  },

  supporters: {
    root: '/supporters',
    all: '/supporters',
    invitations: '/supporters/invitations',
    invite: '/supporters/invite',
  },

  help: '/help',

  staff: {
    root: '/staff/organizations',
    organizations: '/staff/organizations',
    newOrganization: '/staff/organizations/new',
    /**
     * One organization's account. Called with ':id' it is the route pattern,
     * so the address and the pattern cannot drift apart.
     */
    organization: (id: string) => `/staff/organizations/${id}`,
    /** The audit log across every organization. */
    activity: '/staff/activity',
  },

  account: {
    profile: '/account/profile',
    account: '/account',
    users: '/account/users',
    billing: '/account/billing',
    activity: '/account/activity',
  },
} as const;
